import React from 'react';
import { EditorContent } from '@tiptap/react';
import { Code2, Eye, FileCheck2, X } from 'lucide-react';
import type { ComposerApi, ComposerMode } from './useComposerBody';
import { EmailToolbar } from './EmailToolbar';

export interface ComposerBodyProps {
  composer: ComposerApi;
  /** Minimum height (px) of the editing surface. */
  minHeight?: number;
  placeholder?: string;
  /** Extra controls rendered right of the toolbar (e.g. Insert Field picker). */
  fieldPickerSlot?: React.ReactNode;
  /** Extra tools rendered right of the toolbar (e.g. AI improve). */
  toolbarExtraSlot?: React.ReactNode;
}

function ModeBadge({ mode }: { mode: ComposerMode }) {
  const isVisual = mode === 'visual';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider rounded-full border ${
        isVisual
          ? 'bg-blue-50 text-blue-700 border-blue-200'
          : 'bg-slate-100 text-slate-700 border-slate-300'
      }`}
      title={isVisual ? 'WYSIWYG editing mode' : 'Raw HTML editing mode'}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isVisual ? 'bg-brand-primary' : 'bg-amber-400'}`} />
      {isVisual ? 'WYSIWYG' : 'HTML'}
    </span>
  );
}

/**
 * The shared email composer surface: toolbar + WYSIWYG/HTML editing area.
 *
 * Used by BOTH the individual applicant composer and the batch campaign
 * composer — one code path for template insertion, formatting, font
 * size/colour, mode toggling and placeholder preservation.
 */
export function ComposerBody({
  composer,
  minHeight = 220,
  placeholder = 'Write your message here…',
  fieldPickerSlot,
  toolbarExtraSlot,
}: ComposerBodyProps) {
  const { editor, mode, setMode, disabled, templateInserted, dismissTemplateInserted } = composer;
  const isVisual = mode === 'visual';
  // Recipient preview: renders the exact sanitized send payload
  // (composer.getSendHtml()) in a 640px email frame — what the admin
  // sees here is byte-identical to what recipients receive.
  const [previewing, setPreviewing] = React.useState(false);
  const previewDoc = previewing
    ? `<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:24px;background:#eef1f5;font-family:Arial,Helvetica,sans-serif;">` +
      `<div style="max-width:640px;margin:0 auto;background:#ffffff;padding:28px;border-radius:8px;">${composer.getSendHtml()}</div></body></html>`
    : '';

  return (
    <div className="border border-slate-300 focus-within:border-brand rounded-xl overflow-hidden shadow-sm bg-white">
      {/* Toolbar row */}
      <div className="flex items-center gap-1 px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex-wrap">
        <div className="flex-1 min-w-0">
          {previewing ? (
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
              Preview — as recipients will see it
            </span>
          ) : (
            <EmailToolbar composer={composer} />
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {templateInserted && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
              title={`Inserted ${new Date(templateInserted.at).toLocaleTimeString()}`}
            >
              <FileCheck2 className="h-3 w-3" />
              <span className="max-w-[140px] truncate">Template: {templateInserted.name}</span>
              <button
                type="button"
                onClick={dismissTemplateInserted}
                className="text-emerald-400 hover:text-emerald-700 cursor-pointer"
                aria-label="Dismiss template indicator"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          <ModeBadge mode={mode} />
          {!previewing && toolbarExtraSlot}
          {!previewing && fieldPickerSlot}
          <button
            type="button"
            onClick={() => setPreviewing((p) => !p)}
            disabled={disabled}
            className={`px-2.5 py-1 text-xs font-bold rounded-lg flex items-center gap-1 transition-all cursor-pointer disabled:opacity-50 ${
              previewing
                ? 'bg-success text-white hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)]'
                : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
            }`}
            title={previewing ? 'Back to editing' : 'Preview the email exactly as recipients will see it'}
          >
            <Eye className="h-3.5 w-3.5" />
            <span>{previewing ? 'Edit' : 'Preview'}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setPreviewing(false);
              setMode(isVisual ? 'code' : 'visual');
            }}
            disabled={disabled}
            className={`px-2.5 py-1 text-xs font-bold rounded-lg flex items-center gap-1 transition-all cursor-pointer disabled:opacity-50 ${
              isVisual
                ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
            }`}
            title={isVisual ? 'Switch to raw HTML source' : 'Switch back to visual editor'}
          >
            {isVisual ? (
              <>
                <Code2 className="h-3.5 w-3.5" />
                <span>HTML</span>
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5" />
                <span>Visual</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Editing / preview surface */}
      {previewing ? (
        <div className="bg-slate-100 overflow-y-auto" style={{ minHeight }}>
          <iframe
            title="Email preview"
            data-testid="composer-preview-frame"
            srcDoc={previewDoc}
            sandbox=""
            className="w-full border-0 block"
            style={{ minHeight: Math.max(minHeight, 320) }}
          />
        </div>
      ) : (
        <>
          {/* The WYSIWYG surface stays MOUNTED (CSS-hidden) in code mode
              (PR-11 §D): unmounting destroyed the tiptap doc, its undo
              history and the selection on every toggle. */}
          <div
            className={`bg-white px-4 py-3 overflow-y-auto relative ${isVisual ? '' : 'hidden'}`}
            style={{ minHeight }}
            aria-hidden={!isVisual}
          >
            {editor ? (
              <EditorContent
                editor={editor}
                // Canvas typography (PR-11 live-walk Finding 2): headings,
                // quotes, code and list markers must LOOK like what they
                // are while editing — Tailwind preflight otherwise renders
                // an <h2> indistinguishable from body text and strips
                // list markers. This is editor-surface CSS only; the send
                // payload (getSendHtml) never carries these classes.
                className="[&_.tiptap]:outline-none [&_.tiptap]:min-h-[180px] [&_.tiptap]:text-sm [&_.tiptap]:leading-relaxed [&_.tiptap]:text-slate-800 [&_.tiptap_p]:my-2 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_.tiptap_h1]:text-[22px] [&_.tiptap_h1]:font-extrabold [&_.tiptap_h1]:leading-snug [&_.tiptap_h1]:mt-3 [&_.tiptap_h1]:mb-2 [&_.tiptap_h2]:text-[18px] [&_.tiptap_h2]:font-bold [&_.tiptap_h2]:leading-snug [&_.tiptap_h2]:mt-3 [&_.tiptap_h2]:mb-1.5 [&_.tiptap_h3]:text-[15px] [&_.tiptap_h3]:font-bold [&_.tiptap_h3]:mt-2.5 [&_.tiptap_h3]:mb-1 [&_.tiptap_blockquote]:border-l-4 [&_.tiptap_blockquote]:border-slate-300 [&_.tiptap_blockquote]:pl-3 [&_.tiptap_blockquote]:ml-1 [&_.tiptap_blockquote]:italic [&_.tiptap_blockquote]:text-slate-600 [&_.tiptap_pre]:bg-slate-100 [&_.tiptap_pre]:text-slate-800 [&_.tiptap_pre]:rounded-lg [&_.tiptap_pre]:p-3 [&_.tiptap_pre]:font-mono [&_.tiptap_pre]:text-xs [&_.tiptap_pre]:my-2 [&_.tiptap_code]:bg-slate-100 [&_.tiptap_code]:rounded [&_.tiptap_code]:px-1 [&_.tiptap_code]:font-mono [&_.tiptap_code]:text-[12px] [&_.tiptap_ul]:list-disc [&_.tiptap_ul]:pl-5 [&_.tiptap_ul]:my-2 [&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:pl-5 [&_.tiptap_ol]:my-2 [&_.tiptap_li]:my-0.5"
              />
            ) : (
              <div className="text-xs text-slate-500 py-8 text-center">Loading editor…</div>
            )}
            {isVisual && !composer.bodyHtml.trim() && (
              <div className="absolute top-3 left-4 text-xs text-slate-500 italic pointer-events-none">
                {placeholder}
              </div>
            )}
          </div>
          {!isVisual && (
            <textarea
              ref={composer.codeTextareaRef}
              value={composer.bodyHtml}
              onChange={(e) => composer.setRawBodyHtml(e.target.value)}
              disabled={disabled}
              spellCheck={false}
              placeholder="<!-- Raw HTML source — placeholders like {{first_name}} are preserved verbatim -->"
              className="w-full bg-slate-50 text-slate-800 font-mono text-xs leading-relaxed p-4 outline-none resize-y border-0"
              style={{ minHeight }}
              aria-label="Email HTML source"
            />
          )}
        </>
      )}
    </div>
  );
}
